<template>
  <!--
    Панель настроек. Открывается поверх бокового меню и своим закрытием его не
    трогает: пришли из меню — вернёмся в меню, пришли с дашборда — на дашборд.
    Поэтому состоянием меню отсюда не управляем вовсе.

    Подложка закрывает панель по клику. Клавиатурный путь у неё есть, но другой —
    Escape, он висит на документе: вешать обработчик клавиш на саму подложку
    бессмысленно, фокус на ней никогда не окажется.
  -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <aside
    class="settings-aside"
    @click.self="close"
  >
    <div class="settings-panel">
      <!-- Своей шапки у панели нет: заголовок и крестик живут в шапке
           приложения, она выше по z-index и рисуется поверх. -->
      <!-- Обёртка задаёт высоту: у .upCard глобально height: 100%, и без
           родителя с высотой по содержимому карточка растянется на всю панель. -->
      <div class="settings-card">
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.language') }}</span>
            <div class="settings-row__control">
              <up-button @click="changeLocale">
                {{ otherLangLabel }}
              </up-button>
            </div>
          </div>
        </up-card>
      </div>

      <div class="settings-card">
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.notifications') }}</span>
            <div class="settings-row__control">
              <up-button
                :disabled="pushBusy || !pushActionable"
                @click="togglePush"
              >
                {{ pushButtonLabel }}
              </up-button>
            </div>
          </div>

          <p
            v-if="pushHint"
            class="settings-hint"
          >
            {{ pushHint }}
            <button
              v-if="pushState === 'denied'"
              type="button"
              class="settings-hint__link"
              @click="howToAsked = true"
            >
              {{ $t('settings.push_howto') }}
            </button>
          </p>
        </up-card>
      </div>

      <div
        v-if="authorized"
        class="settings-card settings-card--bottom"
      >
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.account') }}</span>
            <div class="settings-row__control">
              <up-button @click="askLogout">
                {{ $t('settings.logout') }} <i class="exu-log-out" />
              </up-button>
            </div>
          </div>
        </up-card>
      </div>
    </div>

    <popup-card
      v-if="howToAsked"
      :is-open="howToAsked"
      @show="howToAsked = false"
    >
      <div class="settings-confirm">
        <p class="settings-confirm__text">{{ $t('settings.push_howto_text') }}</p>
      </div>
    </popup-card>

    <popup-card
      v-if="logoutAsked"
      :is-open="logoutAsked"
      @show="logoutAsked = false"
    >
      <div class="settings-confirm">
        <p class="settings-confirm__text">{{ $t('settings.logout_question') }}</p>
        <div class="settings-confirm__actions">
          <up-button @click="logoutAsked = false">
            {{ $t('settings.cancel') }}
          </up-button>
          <up-button
            :disabled="loggingOut"
            @click="confirmLogout"
          >
            {{ $t('settings.logout') }}
          </up-button>
        </div>
      </div>
    </popup-card>
  </aside>
</template>

<script>
import push from '@/components/mixins/methodsPush';

export default {
  name: 'settingsAside',
  mixins: [push],
  props: {
    locale: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      logoutAsked: false,
      loggingOut: false,
      howToAsked: false,
      pushBusy: false,
      pushPermissionState: 'default',
      pushSubscribed: false,
    };
  },
  computed: {
    // Кнопка называется языком, на который переключит, а не текущим.
    // Так было и у прежней кнопки в меню.
    otherLangLabel() {
      return this.locale === 'ru' ? 'Eng' : 'Рус';
    },
    authorized() {
      return !!this.$store.getters.getToken;
    },
    /**
     * Четыре состояния, а не два.
     *
     * unsupported — браузер не умеет push вовсе. Это весь iOS, пока приложение
     *               не установлено на домашний экран.
     * denied      — человек уже отказал в системном окне. Необратимо из кода:
     *               второй раз браузер спросить не даст никому.
     * on / off    — подписан или нет.
     */
    pushState() {
      if (!this.pushSupported()) return 'unsupported';
      if (this.pushPermissionState === 'denied') return 'denied';

      return this.pushSubscribed ? 'on' : 'off';
    },
    pushActionable() {
      return this.pushState === 'on' || this.pushState === 'off';
    },
    pushButtonLabel() {
      return this.pushState === 'on'
        ? this.$t('settings.push_disable')
        : this.$t('settings.push_enable');
    },
    pushHint() {
      if (this.pushState === 'unsupported') return this.$t('settings.push_unsupported');
      if (this.pushState === 'denied') return this.$t('settings.push_blocked');

      return '';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.refreshPushState();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    },
    changeLocale() {
      this.$emit('switch-locale', this.locale === 'ru' ? 'en' : 'ru');
    },
    refreshPushState() {
      this.pushPermissionState = this.pushPermission();

      return this.currentPushSubscription()
        .then((subscription) => {
          this.pushSubscribed = !!subscription;
        });
    },
    togglePush() {
      if (!this.pushActionable) return Promise.resolve();

      this.pushBusy = true;

      const action = this.pushState === 'on'
        ? this.unsubscribePush()
        // Разрешение спрашиваем прямо здесь: это обработчик нажатия, и только
        // из него браузер согласен показать своё окно.
        : this.requestPushPermission().then((permission) => {
          this.pushPermissionState = permission;

          return permission === 'granted' ? this.subscribePush() : false;
        });

      return action
        .then(() => this.refreshPushState())
        .finally(() => {
          this.pushBusy = false;
        });
    },
    askLogout() {
      this.logoutAsked = true;
    },
    // Сам выход делает App.vue: там и роутер, и миксин входа. Панель только
    // спрашивает и блокирует кнопку, пока снимается подписка.
    confirmLogout() {
      this.loggingOut = true;
      this.$emit('logout');
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

// Панель меню лежит на z-index 200, эта должна быть выше.
.settings-aside {
  z-index: 210;
}

.settings-panel {
  display: flex;
  flex-direction: column;
  gap: $padding-step * 3;
  width: 100%;
  max-width: 420px;
  height: 100%;
  padding: $padding-step * 3;
  overflow-y: auto;
  background: $color-base-secondary;
}

// Высота по содержимому. Карточка внутри тянется на 100% уже от неё.
.settings-card {
  flex: 0 0 auto;
}

// Выход всегда последний. Свободное место забирает верхний отступ, поэтому
// карточка стоит внизу, пока настройки помещаются на экран, и просто уезжает
// вниз за границу, когда их станет больше — тогда до неё надо будет доскроллить.
.settings-card--bottom {
  margin-top: auto;
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $padding-step * 3;
}

.settings-row__label {
  color: $color-text;
}

.settings-hint {
  margin: $padding-step * 2 0 0;
  font-size: 13px;
  color: $color-disabled;
}

.settings-hint__link {
  padding: 0;
  font: inherit;
  color: $color-primary;
  text-decoration: underline;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.settings-confirm {
  display: flex;
  flex-direction: column;
  gap: $padding-step * 4;
}

.settings-confirm__text {
  margin: 0;
  color: $color-text;
}

.settings-confirm__actions {
  display: flex;
  gap: $padding-step * 3;

  :deep(.up-btn) {
    border-radius: $border-radius-inside-large;
  }
}

// Кнопка растягивается на всю ширину родителя — ширину задаёт обёртка,
// а не переопределение стилей самой кнопки.
// Ширина по содержимому, но не уже соседей: кнопки в разных строках должны
// выглядеть одним рядом, а «Выйти» с иконкой шире, чем «Eng».
.settings-row__control {
  flex: 0 0 auto;
  min-width: 92px;

  // Прямые углы кнопки спорят с сильным скруглением карточки.
  :deep(.up-btn) {
    gap: $padding-step * 2;
    padding: $padding-step $padding-step * 3;
    border-radius: $border-radius-inside-large;
  }
}
</style>
